import Cookies from 'js-cookie';

import {ENV} from '@/constants/EnvConstants';
import {isLocalhost, isProduction} from '@/utils/environment';
import logError from '@/utils/errors/logError';

import {C_WAF_PROTECTION} from './WafProtectionConstants';

declare const AwsWafIntegration: any;

class WafProtectionService {
  public countWafError = 0;
  public isScriptLoaded = false;
  public isProd = isProduction;

  public getTokenFromCookie = (): string =>
    Cookies.get(C_WAF_PROTECTION.COOKIE_AWS_WAF_TOKEN) ?? '';

  public loadScript = () => {
    if (isLocalhost) {
      console.info('Running in localhost mode, skipping WAF script loading.');
      return;
    }

    const src = `https://${ENV.VITE_WAF_IDENTIFIER}.${ENV.VITE_WAF_REGION}.sdk.awswaf.com/${ENV.VITE_WAF_IDENTIFIER}/${ENV.VITE_WAF_TOKEN}/challenge.js`;
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
      this.isScriptLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.defer = true;

    script.onload = () => (this.isScriptLoaded = true);
    script.onerror = error => {
      logError(error, {
        customTitle: 'WafProtectionService Script OnError',
        fileName: 'WafProtectionService.ts',
        functionName: 'script.onError',
        data: {
          errorEvent: error,
          scriptSrc: script.src,
        },
      });
      this.isScriptLoaded = false;
      console.error('WafProtectionService.ts script onerror', error);
    };

    document.head.appendChild(script);
  };

  public getWafToken = async (): Promise<string> => {
    if (!this.isScriptLoaded) {
      console.warn('WAF Script not loaded yet.');
      return this.getTokenFromCookie();
    }

    if (typeof AwsWafIntegration !== 'undefined' && AwsWafIntegration) {
      try {
        const token = await AwsWafIntegration.getToken();
        return token || this.getTokenFromCookie();
      } catch (error) {
        logError(error, {
          customTitle: 'Error getting WAF token',
          fileName: 'WafProtectionService',
          functionName: 'getWafToken',
        });

        return this.getTokenFromCookie();
      }
    }

    return this.getTokenFromCookie();
  };

  public incrementWafCount = () => this.countWafError++;
  public resetWafCount = () => (this.countWafError = 0);
  public getCountError = (): number => this.countWafError;

  public getHeadersWithAwsToken = (
    currentHeaders: Headers,
    awsToken: string,
  ): Headers => {
    currentHeaders.set('x-aws-waf-token', awsToken);
    return currentHeaders;
  };

  public getHeadersWithSkipWaf = (currentHeaders: Headers): Headers => {
    currentHeaders.set('x-modak-skip-waf', ENV.VITE_WAF_SKIP_KEY);
    return currentHeaders;
  };

  public isModakUrl = (url: string): boolean =>
    C_WAF_PROTECTION.MODAK_API_PARTIAL_URLS.some(partialUrl =>
      url.includes(partialUrl),
    );

  public canSkipWaf = (url: string): boolean => {
    return !this.isProd && this.isModakUrl(url);
  };

  public isChallenge = (url: string, response: Response): boolean => {
    return (
      this.isModakUrl(url) &&
      response?.status === 202 &&
      response?.headers.get(C_WAF_PROTECTION.HEADERS.ACTION.KEY) ===
        C_WAF_PROTECTION.HEADERS.ACTION.VALUES.CHALLENGE
    );
  };

  public hasModakWaf = (headers: Headers): boolean => {
    return (
      headers.get(C_WAF_PROTECTION.HEADERS.MODAK_WAF.KEY) ===
      C_WAF_PROTECTION.HEADERS.MODAK_WAF.VALUES.TRUE
    );
  };
}

export const wafProtectionService = new WafProtectionService();
